import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router'

import { env } from '@/config/env'
import { Spinner } from '@/design'
import { AuthLayout } from '@/features/auth/auth-layout'
import { AppShell } from '@/features/shell/app-shell'
import { NotFoundPage, RouteErrorPage } from '@/features/shell/error-pages'
import {
  RequireAnonymous,
  RequireLockedOut,
  RequirePermission,
  RequireSession,
  SessionGate,
} from './guards'
import { platformRoutes } from './platform-routes'
import { settingsRoutes } from './settings-routes'

const LoginPage = lazy(() => import('@/features/auth/login-page'))
const SignupPage = lazy(() => import('@/features/auth/signup-page'))
const PricingPage = lazy(() => import('@/features/pricing/pricing-page'))
const LegalPage = lazy(() => import('@/features/legal/legal-page'))
const WelcomePage = lazy(() => import('@/features/onboarding/welcome-page'))
const InviteAcceptPage = lazy(() => import('@/features/auth/invite-page'))
const NoAccessPage = lazy(() => import('@/features/auth/no-access-page'))
const PasswordPages = lazy(() =>
  import('@/features/auth/password-pages').then((m) => ({ default: m.ForgotPasswordPage })),
)
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/password-pages').then((m) => ({ default: m.ResetPasswordPage })),
)
const HomePage = lazy(() => import('@/features/home/home-page'))
const NotificationsPage = lazy(() => import('@/features/notifications/notifications-page'))
const CustomersPage = lazy(() => import('@/features/customers/customers-page'))
const CustomerDetailPage = lazy(() => import('@/features/customers/customer-detail-page'))
const CalendarPage = lazy(() => import('@/features/appointments/calendar-page'))
const AppointmentDetailPage = lazy(() => import('@/features/appointments/appointment-detail-page'))
const WaitlistPage = lazy(() => import('@/features/waitlist/waitlist-page'))
const RecoveryQueuePage = lazy(() => import('@/features/recovery/recovery-queue-page'))
const RecoveryCasePage = lazy(() => import('@/features/recovery/case-page'))
const InboxPage = lazy(() => import('@/features/inbox/inbox-page'))
const ThreadPage = lazy(() => import('@/features/inbox/thread-page'))
const SimulatorPage = lazy(() => import('@/features/inbox/simulator-page'))
const ReportsPage = lazy(() => import('@/features/reports/reports-page'))
const DuebackPage = lazy(() => import('@/features/dueback/dueback-page'))
const KitchenSinkPage = lazy(() => import('@/dev/kitchen-sink'))
const ActionPage = lazy(() => import('@/features/actions/action-page'))
const PublicOrgPage = lazy(() => import('@/features/public/public-org-page'))
const PublicBookPage = lazy(() => import('@/features/public/public-book-page'))
const PublicWaitlistPage = lazy(() => import('@/features/public/public-waitlist-page'))

function Pending() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner />
    </div>
  )
}

const lazyElement = (node: React.ReactNode) => <Suspense fallback={<Pending />}>{node}</Suspense>

export const router = createBrowserRouter([
  // Customer links live outside the staff session gate: no bootstrap wait, no session involvement.
  { path: '/a/:token', element: lazyElement(<ActionPage />), errorElement: <RouteErrorPage /> },
  // RecoupSlots P12 — the public tenant pages (plan §11) on the P11 public API. Like action links,
  // they need no session and sit outside the gate; they send no staff credentials.
  { path: '/o/:slug', element: lazyElement(<PublicOrgPage />), errorElement: <RouteErrorPage /> },
  {
    path: '/o/:slug/book',
    element: lazyElement(<PublicBookPage />),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/o/:slug/waitlist',
    element: lazyElement(<PublicWaitlistPage />),
    errorElement: <RouteErrorPage />,
  },
  // The public site (RecoupSlots launch, plan §11): pricing reads the public plan catalog; the
  // legal and SMS-program pages are static. All carry their own full-page layout and need no
  // session, so they sit outside the gate. The marketing page at `/` is served by RequireSession
  // (D14): signed out on the platform host → marketing; signed in → the dashboard.
  { path: '/pricing', element: lazyElement(<PricingPage />), errorElement: <RouteErrorPage /> },
  {
    path: '/privacy',
    element: lazyElement(<LegalPage kind="privacy" />),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/terms',
    element: lazyElement(<LegalPage kind="terms" />),
    errorElement: <RouteErrorPage />,
  },
  {
    path: '/sms',
    element: lazyElement(<LegalPage kind="sms" />),
    errorElement: <RouteErrorPage />,
  },
  { path: '/welcome', element: lazyElement(<WelcomePage />), errorElement: <RouteErrorPage /> },
  {
    element: <SessionGate />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <RequireAnonymous />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: '/login', element: lazyElement(<LoginPage />) },
              { path: '/signup', element: lazyElement(<SignupPage />) },
              { path: '/forgot-password', element: lazyElement(<PasswordPages />) },
              { path: '/reset-password', element: lazyElement(<ResetPasswordPage />) },
              { path: '/invite/accept', element: lazyElement(<InviteAcceptPage />) },
            ],
          },
        ],
      },
      {
        element: <RequireLockedOut />,
        children: [
          {
            element: <AuthLayout />,
            children: [{ path: '/no-access', element: lazyElement(<NoAccessPage />) }],
          },
        ],
      },
      {
        element: <RequireSession />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: lazyElement(<HomePage />) },
              {
                path: '/notifications',
                element: lazyElement(
                  <RequirePermission requires={['notifications:read']}>
                    <NotificationsPage />
                  </RequirePermission>,
                ),
              },
              ...settingsRoutes(lazyElement),
              ...platformRoutes(lazyElement),
              {
                path: '/customers',
                element: lazyElement(
                  <RequirePermission requires={['customers:read']}>
                    <CustomersPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/customers/:id',
                element: lazyElement(
                  <RequirePermission requires={['customers:read']}>
                    <CustomerDetailPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/calendar',
                element: lazyElement(
                  <RequirePermission requires={['appointments:read']}>
                    <CalendarPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/appointments/:id',
                element: lazyElement(
                  <RequirePermission requires={['appointments:read']}>
                    <AppointmentDetailPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/reports',
                element: lazyElement(
                  <RequirePermission requires={['reports:read']}>
                    <ReportsPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/due-back',
                element: lazyElement(
                  <RequirePermission requires={['reports:read']}>
                    <DuebackPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/inbox',
                element: lazyElement(
                  <RequirePermission requires={['conversations:read']}>
                    <InboxPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/inbox/simulator',
                element: lazyElement(
                  <RequirePermission requires={['conversations:simulate']}>
                    <SimulatorPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/inbox/:id',
                element: lazyElement(
                  <RequirePermission requires={['conversations:read']}>
                    <ThreadPage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/recovery',
                element: lazyElement(
                  <RequirePermission requires={['recovery-cases:read']}>
                    <RecoveryQueuePage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/recovery/:id',
                element: lazyElement(
                  <RequirePermission requires={['recovery-cases:read']}>
                    <RecoveryCasePage />
                  </RequirePermission>,
                ),
              },
              {
                path: '/waitlist',
                element: lazyElement(
                  <RequirePermission requires={['waitlist:read']}>
                    <WaitlistPage />
                  </RequirePermission>,
                ),
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
      // `import.meta.env.VITE_APP_ENV` is replaced at BUILD time, so a production build drops the
      // route and its chunk entirely; the runtime `appEnv` check still covers an unset variable.
      ...(import.meta.env.VITE_APP_ENV === 'production' || env.appEnv === 'production'
        ? []
        : [{ path: '/__kitchen-sink', element: lazyElement(<KitchenSinkPage />) }]),
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
